/**
 * SaleAuthorityProvider — SINGLE frontend authority state (WO#4a §11).
 *
 * Observes the identity of the current active Sale from every backend
 * response that carries it:
 *   - GET /api/v1/sale/config             (primary, polled)
 *   - GET /api/v1/sale/quote (response.sale_authority — WO#4a §10)
 *
 * The provider exposes `currentSaleAuthority` synchronously via React
 * context; consumers derive `overlayRenderable` in the render pass, so
 * a V3-quote observation invalidates the V2 overlay in the SAME commit
 * that surfaces the V3 quote (WO#4a §12 zero-render stale guarantee).
 */
import * as React from "react";

import { saleApi } from "../lib/sale-api";
import type { SaleConfig } from "../lib/sale-types";

export interface CurrentSaleAuthority {
  chain_id: number;
  sale_contract_address: string;
}

export interface SaleAuthorityContextValue {
  authority: CurrentSaleAuthority | null;
  /** Bump this from any component that observes a fresh authority in
   *  a foreign response (e.g. a quote whose `sale_authority` differs
   *  from the currently-cached one). The provider will refetch config
   *  and normalise state.
   */
  observeAuthority: (obs: CurrentSaleAuthority | null) => void;
}

const noopObserve = () => {};

const SaleAuthorityContext = React.createContext<SaleAuthorityContextValue>({
  authority: null,
  observeAuthority: noopObserve,
});

function equalAuthority(a: CurrentSaleAuthority | null, b: CurrentSaleAuthority | null): boolean {
  if (a === null || b === null) return a === b;
  return (
    a.chain_id === b.chain_id
    && a.sale_contract_address.toLowerCase() === b.sale_contract_address.toLowerCase()
  );
}

function extractAuthorityFromConfig(cfg: SaleConfig): CurrentSaleAuthority | null {
  if (cfg.chain_id == null || cfg.sale_contract_address == null) return null;
  return {
    chain_id: cfg.chain_id,
    sale_contract_address: cfg.sale_contract_address,
  };
}

const POLL_INTERVAL_MS = 30_000;

export function SaleAuthorityProvider(props: { children: React.ReactNode }) {
  const [authority, setAuthority] = React.useState<CurrentSaleAuthority | null>(null);
  // Ref-mirrored value for use inside async callbacks without stale closures.
  const authorityRef = React.useRef<CurrentSaleAuthority | null>(null);
  authorityRef.current = authority;

  const refresh = React.useCallback(async () => {
    try {
      const cfg = await saleApi.config();
      const next = extractAuthorityFromConfig(cfg);
      if (!equalAuthority(authorityRef.current, next)) {
        // Atomic React state update — this transitions
        // `currentSaleAuthority` in a single commit; derived
        // `overlayRenderable` in the render pass evaluates against
        // the new value immediately, so no frame can commit V3
        // quote + V2 overlay together (see App.tsx render guard).
        setAuthority(next);
      }
    } catch {
      // /config unreachable → keep the last observed authority. This
      // is intentional: purchase authority is independent of the
      // manifest fetch, and the overlay is presentation-only.
    }
  }, []);

  // Initial fetch + poll + focus refresh.
  React.useEffect(() => {
    let cancelled = false;
    void refresh();
    const interval = window.setInterval(() => {
      if (!cancelled) void refresh();
    }, POLL_INTERVAL_MS);
    const onFocus = () => {
      if (!cancelled) void refresh();
    };
    // WO#4a §10 — listen for authority observations from quote responses
    // via the decoupled CustomEvent. Synchronous invalidation happens
    // via `observeAuthority` which updates React state atomically.
    const onObserved = (evt: Event) => {
      const detail = (evt as CustomEvent).detail as
        | { chain_id?: unknown; sale_contract_address?: unknown }
        | undefined;
      if (
        !detail
        || typeof detail.chain_id !== "number"
        || typeof detail.sale_contract_address !== "string"
      ) return;
      const next: CurrentSaleAuthority = {
        chain_id: detail.chain_id,
        sale_contract_address: detail.sale_contract_address,
      };
      if (!equalAuthority(authorityRef.current, next)) {
        setAuthority(next);
        if (!cancelled) void refresh();
      }
    };
    window.addEventListener("focus", onFocus);
    window.addEventListener("thegrid:sale-authority-observed", onObserved as EventListener);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("thegrid:sale-authority-observed", onObserved as EventListener);
    };
  }, [refresh]);

  // External observers (e.g. useSaleQuote) push authority via observeAuthority.
  // If the observation reveals a *different* Sale, we synchronously update
  // the authority state so the very next render evaluates the manifest
  // identity against the new value.
  const observeAuthority = React.useCallback((obs: CurrentSaleAuthority | null) => {
    if (obs == null) return;
    if (!equalAuthority(authorityRef.current, obs)) {
      setAuthority(obs);
      // Also kick a fresh /config refetch in the background so the
      // provider stays coherent with server-side registry state.
      void refresh();
    }
  }, [refresh]);

  const value = React.useMemo<SaleAuthorityContextValue>(
    () => ({ authority, observeAuthority }),
    [authority, observeAuthority],
  );

  return (
    <SaleAuthorityContext.Provider value={value}>
      {props.children}
    </SaleAuthorityContext.Provider>
  );
}

export function useCurrentSaleAuthority(): CurrentSaleAuthority | null {
  return React.useContext(SaleAuthorityContext).authority;
}

export function useObserveSaleAuthority(): (obs: CurrentSaleAuthority | null) => void {
  return React.useContext(SaleAuthorityContext).observeAuthority;
}
